// src/modules/PackageManager/CustomAppTab.tsx
import React, { useState } from 'react';
import { Plus, Loader2 } from 'lucide-react';
import { useToast } from '../../providers/ToastProvider/ToastContext';
import { addCustomApp } from '../../components/services/PackageManager';

const CustomAppTab: React.FC = () => {
  const { addToast } = useToast();
  const [loading, setLoading] = useState(false);
  const [customForm, setCustomForm] = useState({
    packageIdentifier: '',
    packageVersion: '',
    packageName: '',
    architecture: 'x64',
    installerUrl: '',
  });

  const resetCustomForm = () => {
    setCustomForm({
      packageIdentifier: '',
      packageVersion: '',
      packageName: '',
      architecture: 'x64',
      installerUrl: '',
    });
  };

  const handleCustomSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!customForm.packageIdentifier.trim()) return addToast('Package Identifier is required', 'error');
    if (!customForm.packageVersion.trim()) return addToast('Package Version is required', 'error');
    if (!customForm.packageName.trim()) return addToast('Package Name is required', 'error');
    if (!customForm.architecture.trim()) return addToast('Architecture is required', 'error');
    if (!customForm.installerUrl.trim()) return addToast('Installer URL is required', 'error');

    try {
      new URL(customForm.installerUrl);
    } catch {
      addToast('Please enter a valid URL for the installer', 'error');
      return;
    }

    setLoading(true);
    try {
      const response = await addCustomApp({
        packageIdentifier: customForm.packageIdentifier.trim(),
        packageVersion: customForm.packageVersion.trim(),
        packageName: customForm.packageName.trim(),
        architecture: customForm.architecture.trim(),
        installerUrl: customForm.installerUrl.trim(),
      });

      if (response.isSuccess) {
        addToast('Custom application added successfully', 'success');
        resetCustomForm();
      } else {
        addToast(response.message || 'Failed to add custom application', 'error');
      }
    } catch (error: any) {
      addToast(error?.response?.data?.message || 'Failed to add custom application', 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-[#1C1D1E] p-6 rounded-lg border border-gray-700">
      <div className="mb-4">
        <h2 className="text-lg font-semibold text-white mb-2">Add Custom Application</h2>
        <p className="text-sm text-gray-400">Add a custom application with manual configuration</p>
      </div>

      <form onSubmit={handleCustomSubmit} className="space-y-4">
        <div>
          <label htmlFor="custom-packageIdentifier" className="block text-sm font-medium text-gray-300 mb-2">
            Package Identifier <span className="text-red-500">*</span>
          </label>
          <input
            id="custom-packageIdentifier"
            type="text"
            value={customForm.packageIdentifier}
            onChange={(e) => setCustomForm({ ...customForm, packageIdentifier: e.target.value })}
            placeholder="e.g., Test.Manifest"
            className="w-full bg-[#2A2A2B] border border-gray-600 rounded-lg px-4 py-2.5 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
            disabled={loading}
          />
        </div>

        <div>
          <label htmlFor="custom-packageVersion" className="block text-sm font-medium text-gray-300 mb-2">
            Package Version <span className="text-red-500">*</span>
          </label>
          <input
            id="custom-packageVersion"
            type="text"
            value={customForm.packageVersion}
            onChange={(e) => setCustomForm({ ...customForm, packageVersion: e.target.value })}
            placeholder="e.g., 1.0.0.5927"
            className="w-full bg-[#2A2A2B] border border-gray-600 rounded-lg px-4 py-2.5 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
            disabled={loading}
          />
        </div>

        <div>
          <label htmlFor="custom-packageName" className="block text-sm font-medium text-gray-300 mb-2">
            Package Name <span className="text-red-500">*</span>
          </label>
          <input
            id="custom-packageName"
            type="text"
            value={customForm.packageName}
            onChange={(e) => setCustomForm({ ...customForm, packageName: e.target.value })}
            placeholder="e.g., Testing Update"
            className="w-full bg-[#2A2A2B] border border-gray-600 rounded-lg px-4 py-2.5 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
            disabled={loading}
          />
        </div>

        <div>
          <label htmlFor="custom-architecture" className="block text-sm font-medium text-gray-300 mb-2">
            Architecture <span className="text-red-500">*</span>
          </label>
          <select
            id="custom-architecture"
            value={customForm.architecture}
            onChange={(e) => setCustomForm({ ...customForm, architecture: e.target.value })}
            className="w-full bg-[#2A2A2B] border border-gray-600 rounded-lg px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
            disabled={loading}
          >
            <option value="x64">x64</option>
            <option value="x86">x86</option>
            <option value="arm64">ARM64</option>
            <option value="arm">ARM</option>
          </select>
        </div>

        <div>
          <label htmlFor="custom-installerUrl" className="block text-sm font-medium text-gray-300 mb-2">
            Installer URL <span className="text-red-500">*</span>
          </label>
          <input
            id="custom-installerUrl"
            type="url"
            value={customForm.installerUrl}
            onChange={(e) => setCustomForm({ ...customForm, installerUrl: e.target.value })}
            placeholder="https://example.com/installer.exe"
            className="w-full bg-[#2A2A2B] border border-gray-600 rounded-lg px-4 py-2.5 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
            disabled={loading}
          />
          <p className="text-xs text-gray-500 mt-1">Direct download URL for the application installer</p>
        </div>

        <div className="flex gap-3 pt-4">
          <button
            type="submit"
            disabled={loading}
            className="flex items-center gap-2 px-6 py-2.5 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:bg-gray-600 disabled:cursor-not-allowed transition-colors"
          >
            {loading ? (
              <>
                <Loader2 className="animate-spin" size={18} />
                Adding...
              </>
            ) : (
              <>
                <Plus size={18} />
                Add Custom Application
              </>
            )}
          </button>
          <button
            type="button"
            onClick={resetCustomForm}
            disabled={loading}
            className="px-6 py-2.5 bg-gray-700 text-white rounded-lg hover:bg-gray-600 disabled:bg-gray-800 disabled:cursor-not-allowed transition-colors"
          >
            Clear
          </button>
        </div>
      </form>
    </div>
  );
};

export default CustomAppTab;
